import React, { useEffect, useState } from "react";
import { Button, CloseButton, Modal, Row, Col, Alert } from "react-bootstrap";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import axios from "axios";

interface IProps {
  closeFilterModal: () => void;
  selectedFilterAndValue2: any;
  setSelectedFilterAndValue2: any;
  handleSubmit: (selectedFilterAndValue2: any) => void;
  taskSource?: "email" | "ticket"; // Add taskSource prop
}

const UnassignedFilterModal: React.FC<IProps> = ({
  closeFilterModal,
  selectedFilterAndValue2,
  setSelectedFilterAndValue2,
  handleSubmit,
  taskSource, // Receive taskSource
}) => {
  const [selectedUnassignedFilters, setSelectedUnassignedFilters] = useState<
    string[]
  >([]);
  const [dateTo, setDateTo] = useState<string>("");
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [emailValidationError, setEmailValidationError] = useState<
    string | null
  >(null);

  // Store the initial state when modal opens
  const [initialFilters, setInitialFilters] = useState<string[]>([]);
  const [initialFilterValues, setInitialFilterValues] = useState<any>({});
  const [initialDateTo, setInitialDateTo] = useState<string>("");

  // Define filters based on taskSource - exclude "Id" for emails
  const allFilters = [
    { label: "Id", value: "id" },
    { label: "Customer email", value: "email_id" },
    { label: "Date", value: "created" },
    { label: "Disposition", value: "disposition_name" },
    { label: "Channel", value: "channel" },
  ];

  // Filter out "Id" when taskSource is "email"
  const filters = taskSource === "email" 
    ? allFilters.filter(filter => filter.value !== "id")
    : allFilters;

  useEffect(() => {
    const savedFilters = sessionStorage.getItem("selectedUnassignedFilters");
    const savedFilterValues = sessionStorage.getItem("selectedFilterAndValue2");

    if (savedFilters && savedFilterValues) {
      const parsedFilters = JSON.parse(savedFilters);
      const parsedValues = JSON.parse(savedFilterValues);
      const savedDateTo = parsedValues.created || "";

      // Filter out "id" from saved filters if taskSource is "email"
      const filteredSavedFilters = taskSource === "email"
        ? parsedFilters.filter((f: string) => f !== "id")
        : parsedFilters;

      setSelectedUnassignedFilters(filteredSavedFilters);
      setSelectedFilterAndValue2(parsedValues);
      setDateTo(savedDateTo);

      // Store initial state
      setInitialFilters(filteredSavedFilters);
      setInitialFilterValues(parsedValues);
      setInitialDateTo(savedDateTo);
    } else {
      const appliedFilters = Object.keys(selectedFilterAndValue2).filter(
        (key) => selectedFilterAndValue2[key]
      );
      const currentDateTo = selectedFilterAndValue2.created || "";

      // Filter out "id" from applied filters if taskSource is "email"
      const filteredAppliedFilters = taskSource === "email"
        ? appliedFilters.filter((f: string) => f !== "id")
        : appliedFilters;

      setSelectedUnassignedFilters(filteredAppliedFilters);
      setDateTo(currentDateTo);

      // Store initial state
      setInitialFilters(filteredAppliedFilters);
      setInitialFilterValues(selectedFilterAndValue2);
      setInitialDateTo(currentDateTo);
    }
  }, [taskSource]);

  const handleFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSelectedUnassignedFilters((prevSelectedFilters) => {
      let updatedFilters;
      if (prevSelectedFilters.includes(value)) {
        updatedFilters = prevSelectedFilters.filter(
          (filter) => filter !== value
        );
        setSelectedFilterAndValue2((prev) => ({
          ...prev,
          [value]: "",
        }));
        if (value === "email_id") setEmailValidationError(null);
      } else {
        updatedFilters = [...prevSelectedFilters, value];
      }
      return updatedFilters;
    });
  };

  const handleFilterValueChange = (value: string, filter: string) => {
    setSelectedFilterAndValue2((prevFilterValues: any) => ({
      ...prevFilterValues,
      [filter]: value,
    }));
  };

  const handleDateToChange = (event: any) => {
    const value = event.target.value;
    setDateTo(value);
    setSelectedFilterAndValue2((prev: any) => ({
      ...prev,
      created: value,
    }));
  };

  const resetFilters = () => {
    setSelectedUnassignedFilters([]);
    const emptyFilters = filters.reduce((acc, filter) => {
      acc[filter.value] = "";
      return acc;
    }, {} as any);
    setSelectedFilterAndValue2(emptyFilters);
    setDateTo("");
    setError(null);
    setEmailValidationError(null);

    // Update initial state to empty after reset
    setInitialFilters([]);
    setInitialFilterValues(emptyFilters);
    setInitialDateTo("");

    sessionStorage.removeItem("selectedUnassignedFilters");
    sessionStorage.removeItem("selectedFilterAndValue2");
    handleSubmit({});
  };

  const apiPort = process.env.REACT_APP_API_PROTOCOL;
  const fetchingTenant = window.location.hostname.split(".")[0];

  const searchCustomerEmail = (skey: string) => {
    axios
      .get(
        `${apiPort}://${fetchingTenant}.${process.env.REACT_APP_BASE_URL}/v1/customers/contacts/search-by-email/`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("auth-token")}`,
          },
          params: { email: skey },
        }
      )
      .then((res) => {
        if (res.status === 200) {
          setSuggestions(res.data);
          setError(null);
        }
      })
      .catch(() => {
        setError("Failed to fetch customer email suggestions.");
      });
  };

  // Check if Apply button should be enabled
  const isApplyEnabled = selectedUnassignedFilters.some((filter) => {
    if (filter === "created") {
      return dateTo && dateTo.trim() !== "";
    }
    const value = selectedFilterAndValue2[filter];
    return value && value.toString().trim() !== "";
  });

  // Revert to initial state when closing without applying
  const handleCloseModal = () => {
    // Restore the initial state (what was there when modal opened)
    setSelectedUnassignedFilters(initialFilters);
    setSelectedFilterAndValue2(initialFilterValues);
    setDateTo(initialDateTo);
    setEmailValidationError(null);
    closeFilterModal();
  };

  const handleApplyFilters = () => {
    let nextFilters = [...selectedUnassignedFilters];
    let emailError = null;

    nextFilters = nextFilters.filter((filter) => {
      if (filter === "created") {
        return dateTo && dateTo.trim() !== "";
      }
      const value = selectedFilterAndValue2[filter];
      if (!value || value.toString().trim() === "") {
        return false;
      }
      if (filter === "email_id") {
        const matched = suggestions.find((s) => s.email === value);
        if (!matched) {
          emailError = "Please select a valid email from the list.";
          return false;
        }
      }
      return true;
    });

    setEmailValidationError(emailError);

    if (emailError) return; // Don't close modal on email error

    const filterValuesToSubmit = nextFilters.reduce((acc, filter) => {
      acc[filter] = selectedFilterAndValue2[filter];
      return acc;
    }, {} as any);

    setSelectedUnassignedFilters(nextFilters);
    setSelectedFilterAndValue2(filterValuesToSubmit);

    sessionStorage.setItem(
      "selectedUnassignedFilters",
      JSON.stringify(nextFilters)
    );
    sessionStorage.setItem(
      "selectedFilterAndValue2",
      JSON.stringify(filterValuesToSubmit)
    );

    handleSubmit(filterValuesToSubmit);
    closeFilterModal();
  };

  return (
    <Modal show={true} onHide={handleCloseModal} centered backdrop="static">
      <Modal.Header>
        <Modal.Title className="fw-bold">Filters</Modal.Title>
        <CloseButton onClick={handleCloseModal} />
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        <div className="px-3 py-2">
          {filters.map((filter) => (
            <Row key={filter.value} className="mb-3 align-items-center">
              <Col xs={12} md={4} className="text-nowrap">
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedUnassignedFilters.includes(filter.value)}
                      onChange={handleFilterChange}
                      value={filter.value}
                      color="primary"
                    />
                  }
                  label={filter.label}
                />
              </Col>
              <Col xs={12} md={8}>
                {selectedUnassignedFilters.includes(filter.value) && (
                  <>
                    {filter.value === "created" ? (
                      <TextField
                        id="date-to"
                        label="Select Date"
                        type="date"
                        InputLabelProps={{ shrink: true }}
                        InputProps={{
                          inputProps: { style: { textTransform: "uppercase" } },
                        }}
                        fullWidth
                        value={dateTo}
                        onChange={handleDateToChange}
                      />
                    ) : filter.value === "email_id" ? (
                      <Autocomplete
                        freeSolo
                        options={suggestions.map((option) => option.email)}
                        value={selectedFilterAndValue2[filter.value] || ""}
                        onInputChange={(event, newInputValue) => {
                          searchCustomerEmail(newInputValue);
                          setSelectedFilterAndValue2((prev: any) => ({
                            ...prev,
                            [filter.value]: newInputValue,
                          }));
                          setEmailValidationError(null);
                        }}
                        onChange={(event, newValue) => {
                          const selectedSuggestion = suggestions.find(
                            (suggestion) => suggestion.email === newValue
                          );
                          setSelectedFilterAndValue2((prev: any) => ({
                            ...prev,
                            [filter.value]: selectedSuggestion
                              ? selectedSuggestion.email
                              : newValue || "",
                          }));
                          setEmailValidationError(null);
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={filter.label}
                            variant="outlined"
                            error={!!emailValidationError}
                            helperText={emailValidationError || ""}
                          />
                        )}
                      />
                    ) : (
                      <TextField
                        label={filter.label}
                        fullWidth
                        value={selectedFilterAndValue2[filter.value] || ""}
                        onChange={(e) =>
                          handleFilterValueChange(e.target.value, filter.value)
                        }
                      />
                    )}
                  </>
                )}
              </Col>
            </Row>
          ))}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-primary" onClick={resetFilters}>
          Reset
        </Button>
        <Button
          variant="primary"
          onClick={handleApplyFilters}
          disabled={!isApplyEnabled}
        >
          Apply
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default UnassignedFilterModal;